import { useI18n } from "vue-i18n";
import { codedRefusal } from "@/shared/i18n/describe-error";
import { notificationsMessages } from "./notifications";

/**
 * THE ONE DOOR into the bell's dictionary.
 *
 * `when` rides along because the bell's timestamp used to be a hardcoded
 * `Intl.DateTimeFormat("pt-BR", …)` — the exact assumption ADR-018 refuses, and
 * the kind that survives every test because a date always renders SOMETHING.
 */
/** A origem do aviso de uma regra recusada por um guarda do banco (`event-wiring.ts` no servidor). */
const RULE_REFUSED = "rule_refused";

export function useNotificationsI18n() {
	const { t, te, locale } = useI18n({
		useScope: "local",
		inheritLocale: true,
		messages: notificationsMessages,
	});

	/** The instant a notice arrived, in the language the panel is being read in. */
	function when(iso: string): string {
		return new Intl.DateTimeFormat(locale.value, {
			dateStyle: "short",
			timeStyle: "short",
		}).format(new Date(iso));
	}

	/** The other channel a notice also went out on, in words. */
	function mediumLabel(medium: string): string {
		return te(`medium.${medium}`) ? t(`medium.${medium}`) : medium;
	}

	/** `warning` / `urgent` / `info` in the reader's language; an unknown one is drawn as it came. */
	function severityLabel(severity: string): string {
		return te(`severity.${severity}`) ? t(`severity.${severity}`) : severity;
	}

	/** Recognize the existing system title without replacing custom notification text. */
	function titleLabel(item: {
		subjectKind: string | null;
		title: string;
		origin?: string;
	}): string {
		if (item.origin === RULE_REFUSED) return t("bell.ruleRefused", { rule: item.title });
		return item.subjectKind === "human_request" && item.title === "Um agente precisa de ajuda"
			? t("bell.agentNeedsHelp")
			: item.title;
	}

	/**
	 * A recusa de um guarda chega CODIFICADA (`WINDOW_TAKEN(hall): …`, o servidor não
	 * sabe a língua de quem lê): vira a frase da casa, com o campo pelo rótulo.
	 */
	function bodyLabel(
		item: { body: string | null; origin?: string },
		fieldName?: (code: string) => string | undefined,
	): string | null {
		if (!item.body || item.origin !== RULE_REFUSED) return item.body;
		return codedRefusal(item.body, fieldName) ?? item.body;
	}

	return { t, te, locale, when, mediumLabel, titleLabel, bodyLabel, severityLabel };
}
